/**
 * Terminal Static Data
 *
 * DATA SOURCE DOCUMENTATION - VERIFIED DECEMBER 15, 2025
 * All data in this file comes from verified, authoritative sources.
 *
 * SOURCES USED:
 * - Credit Ratings: Official S&P, Fitch, Moody's press releases
 * - Macro Data: Stat.uz, CBU, World Bank WDI, IMF WEO
 * - FDI Data: UNCTAD, Central Bank of Uzbekistan
 * - Population: UN Population Division, World Bank
 * - Sector Analysis: NATEX proprietary research (clearly marked)
 */

// =============================================================================
// CREDIT RATINGS - Verified from official rating agency press releases
// MAJOR: 2 upgrades + 1 outlook improvement in 2025!
// =============================================================================
export interface RatingData {
  rating: string;
  outlook: string;
  prev: string;
  change: string;
  date: string;
  source: string;
}

export const creditRatings = {
  // S&P: Upgraded to BB from BB- on Nov 21, 2025
  // Source: S&P Global Ratings press release
  sp: {
    rating: 'BB',
    outlook: 'STABLE',
    prev: 'BB-',
    change: '+1',
    date: '2025-11-21',
    source: 'S&P Global Ratings',
  } as RatingData,

  // Fitch: Upgraded to BB from BB- on Jun 26, 2025
  // Source: Fitch Ratings press release
  fitch: {
    rating: 'BB',
    outlook: 'STABLE',
    prev: 'BB-',
    change: '+1',
    date: '2025-06-26',
    source: 'Fitch Ratings',
  } as RatingData,

  // Moody's: Ba3 with Positive outlook (changed from Stable Jun 13, 2025)
  // Source: Moody's Investors Service
  moodys: {
    rating: 'Ba3',
    outlook: 'POS',
    prev: 'STABLE',
    change: '↑',
    date: '2025-06-13',
    source: "Moody's Investors Service",
  } as RatingData,
};

// =============================================================================
// MARKET DATA - Uzbekistan Key Indicators
// Sources: Stat.uz, CBU, World Bank WDI, IMF WEO
// Last verified: December 15, 2025
// =============================================================================
export const marketData = {
  ticker: 'UZB',
  lastUpdate: '2025-12-15',
  lastVerified: '2025-12-15',

  ratings: creditRatings,

  // Macro indicators - Source: Stat.uz, CBU, IMF WEO Dec 2025
  macro: {
    gdp: { value: 115, unit: 'B', yoy: 6.2, source: 'World Bank / IMF' },
    gdpGrowth: { value: 7.6, unit: '%', forecast: 7.3, source: 'Stat.uz 9M 2025' },
    gdpPerCapita: { value: 3100, unit: 'USD', yoy: 8.5, source: 'World Bank WDI' },
    inflation: { value: 7.5, unit: '%', target: 5.0, trend: 'falling', source: 'CBU Nov 2025' },
    fxReserves: { value: 49.7, unit: 'B', months: 11, yoy: 21, source: 'CBU Jun 2025' },
    currentAccount: { value: -5.0, unit: '%GDP', source: 'IMF WEO Dec 2025' },
    fiscalBalance: { value: -3.0, unit: '%GDP', source: 'IMF WEO Dec 2025' },
    publicDebt: { value: 33, unit: '%GDP', source: 'IMF WEO Dec 2025' },
    unemployment: { value: 5.8, unit: '%', source: 'Stat.uz Q3 2025' },
  },

  // Population - Source: UN Population Division, Stat.uz
  population: {
    total: 37.1,
    medianAge: 27,
    under30: 55,
    laborForce: 68.4,
    urban: 50,
    growth: 1.5,
    source: 'UN Population Division / Stat.uz',
  },

  // FDI Data - Source: UNCTAD, Central Bank of Uzbekistan
  // 2024 was record year with $11.9B inflows
  fdi: {
    total2024: 11.9,
    yoy: 53.6,
    q4_2024: 3.87,
    target2025: 42,
    source: 'UNCTAD / Central Bank of Uzbekistan',
    history: [
      { year: '2020', value: 1.7 },
      { year: '2021', value: 2.1 },
      { year: '2022', value: 4.5 },
      { year: '2023', value: 7.7 },
      { year: '2024', value: 11.9 },
    ],
    sources: [
      { country: 'CHN', name: 'China', share: 26.9, value: 3.2 },
      { country: 'RUS', name: 'Russia', share: 17.6, value: 2.1 },
      { country: 'KOR', name: 'South Korea', share: 11.8, value: 1.4 },
      { country: 'TUR', name: 'Turkey', share: 9.2, value: 1.1 },
      { country: 'ARE', name: 'UAE', share: 7.6, value: 0.9 },
      { country: 'EU', name: 'European Union', share: 6.7, value: 0.8 },
    ],
  },

  // Digital economy - Source: ITU, CBU, NATEX estimates
  digital: {
    payments: { value: 10.2, unit: 'B/mo', yoy: 45, source: 'Central Bank of Uzbekistan' },
    fintechUsers: 17,
    unicorns: 2,
    unicornValue: 2.5,
    internetPen: 78,
    mobileBanking: 65,
    sourceNote: 'ITU / CBU / NATEX Estimates',
  },

  // WTO Accession Status - Source: WTO official documents
  wto: {
    status: 'NEGOTIATING',
    target: 'MAR-2026',
    progress: 91,
    bilateral: { done: 31, total: 34 },
    source: 'WTO Working Party documents',
  },
};

// =============================================================================
// SECTOR DATA - NATEX PROPRIETARY ANALYSIS
// These are NATEX proprietary estimates based on market research
// =============================================================================
export const sectorData = [
  { sector: 'FINTECH', size: 6.22, proj2028: 9.49, cagr: 11.1, competition: 'MED', timing: 'NOW', signal: 'BUY' },
  { sector: 'AI/ML', size: 0.15, proj2028: 0.80, cagr: 52.0, competition: 'LOW', timing: 'NOW', signal: 'STRONG BUY' },
  { sector: 'WEB3', size: 0.04, proj2028: 0.25, cagr: 63.0, competition: 'V.LOW', timing: 'NOW', signal: 'STRONG BUY' },
  { sector: 'ECOMM', size: 3.20, proj2028: 7.50, cagr: 23.7, competition: 'HIGH', timing: '6-12M', signal: 'HOLD' },
];
// Source attribution
export const sectorDataSource = 'NATEX Proprietary Research - December 2025';

// =============================================================================
// PEER COMPARISON DATA - Central Asia & Silk Road Corridor
// Sources: IMF WEO Dec 2025, World Bank WDI, Rating Agencies
// Last verified: December 2025
// =============================================================================
export const peerData = [
  { country: 'UZB', gdp: 7.6, fdi: 11.9, rating: 'BB', reserves: 49.7, debt: 33, score: 88 },
  { country: 'KAZ', gdp: 5.5, fdi: 28.0, rating: 'BBB-', reserves: 35.0, debt: 25, score: 72 },
  { country: 'KGZ', gdp: 4.2, fdi: 0.4, rating: 'B', reserves: 3.2, debt: 52, score: 58 },
  { country: 'TJK', gdp: 6.5, fdi: 0.25, rating: 'B-', reserves: 3.8, debt: 46, score: 52 },
  { country: 'AZE', gdp: 4.0, fdi: 4.5, rating: 'BB+', reserves: 12.0, debt: 22, score: 68 },
  { country: 'GEO', gdp: 7.0, fdi: 2.1, rating: 'BB', reserves: 5.2, debt: 40, score: 74 },
  { country: 'MNG', gdp: 5.8, fdi: 2.8, rating: 'B', reserves: 4.5, debt: 65, score: 55 },
];
export const peerDataSource = 'IMF WEO Dec 2025, World Bank WDI, S&P/Fitch/Moodys';
export const peerDataNote = 'Score column is NATEX proprietary calculation';

// =============================================================================
// GDP HISTORY - Central Asia
// Source: World Bank WDI, IMF WEO
// =============================================================================
export const gdpHistory = [
  { year: '2020', uzb: 1.9, kaz: -2.5, geo: -6.8, aze: -4.3 },
  { year: '2021', uzb: 7.4, kaz: 4.3, geo: 10.5, aze: 5.6 },
  { year: '2022', uzb: 5.7, kaz: 3.2, geo: 10.1, aze: 4.6 },
  { year: '2023', uzb: 6.3, kaz: 5.1, geo: 7.5, aze: 1.1 },
  { year: '2024', uzb: 6.5, kaz: 5.2, geo: 7.0, aze: 4.0 },
  { year: '2025E', uzb: 6.2, kaz: 5.5, geo: 5.5, aze: 3.5 },
];
export const gdpHistorySource = 'World Bank WDI / IMF WEO Dec 2025';

// =============================================================================
// OPPORTUNITY SCORE - NATEX PROPRIETARY
// This is NATEX's proprietary scoring methodology
// =============================================================================
export const opportunityScore = {
  overall: 88,
  signal: 'STRONG BUY',
  methodology: 'NATEX Proprietary Scoring Model v2.0',
  lastCalculated: '2025-12-16',
  components: [
    { name: 'Growth Drivers', score: 88, weight: 30, delta: '+3' },
    { name: 'Investment Climate', score: 82, weight: 25, delta: '+5' },
    { name: 'Catalyst Pipeline', score: 95, weight: 20, delta: '+8' },
    { name: 'Risk Profile', score: 76, weight: 15, delta: '+2' },
    { name: 'Valuation Signal', score: 90, weight: 10, delta: '+4' },
  ],
  history: [
    { month: 'JUL', score: 78 },
    { month: 'AUG', score: 80 },
    { month: 'SEP', score: 82 },
    { month: 'OCT', score: 84 },
    { month: 'NOV', score: 86 },
    { month: 'DEC', score: 88 },
  ],
};

// =============================================================================
// TIMELINE - Historical Events (Verified from official sources)
// =============================================================================
export const timeline = [
  { date: '2017-09', event: 'Currency liberalization', type: 'REFORM', source: 'Central Bank of Uzbekistan' },
  { date: '2023-03', event: 'First unicorn (Uzum $1.16B)', type: 'MILESTONE', source: 'TechCrunch / Company announcement' },
  { date: '2024-12', event: 'FDI record $11.9B', type: 'FDI', source: 'UNCTAD / Central Bank' },
  { date: '2025-06', event: 'Fitch upgrade BB', type: 'RATING', source: 'Fitch Ratings' },
  { date: '2025-11', event: 'S&P upgrade BB', type: 'RATING', source: 'S&P Global Ratings' },
  { date: '2026-03', event: 'WTO accession target', type: 'TRADE', future: true, source: 'WTO Working Party' },
];
